/**
 * RedeemCodeModal — web port. Manual entry of WG-XXXX-XXXX-XXXX-XXXX
 * codes with auto-advance between segments. QR scanner is RN-only;
 * not ported. On success, parent should call `startAggressivePolling`
 * to land the new credit quickly.
 */

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import Modal from '../common/Modal';
import { redeemCode } from '../api/billingApi';
import { useLanguage } from '../hooks/useLanguage';
import './RedeemCodeModal.css';

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function RedeemCodeModal({
  visible,
  onClose,
  onSuccess,
}: Props) {
  const { t, selectedLanguage } = useLanguage();
  const [segments, setSegments] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    amount: number;
    asset: string;
  } | null>(null);

  const refs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  useEffect(() => {
    if (!visible) {
      setSegments(['', '', '', '']);
      setError(null);
      setSuccess(null);
    } else {
      setTimeout(() => refs[0].current?.focus(), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const handleSegmentChange = (idx: number, raw: string) => {
    const cleaned = raw
      .replace(/[^A-Z0-9]/gi, '')
      .toUpperCase()
      .slice(0, 4);
    setSegments(prev => {
      const next = [...prev];
      next[idx] = cleaned;
      return next;
    });
    setError(null);
    if (cleaned.length === 4 && idx < 3) {
      refs[idx + 1].current?.focus();
    }
  };

  const handleKeyDown = (idx: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && segments[idx] === '' && idx > 0) {
      refs[idx - 1].current?.focus();
    }
  };

  const handleRedeem = async () => {
    const fullCode = `WG-${segments.join('-')}`;
    const stripped = fullCode.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    if (!stripped || stripped.length !== 18) {
      setError(t('PLEASE_ENTER_COMPLETE_CODE') || 'Enter the complete code');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await redeemCode(fullCode, selectedLanguage);
      setSuccess({
        amount: result.granted.amount,
        asset: result.granted.asset,
      });
      setSegments(['', '', '', '']);
      setTimeout(() => {
        onSuccess?.();
        setSuccess(null);
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          t('FAILED_TO_REDEEM') ||
          'Failed to redeem code',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      title={t('REDEEM_CODE') || 'Redeem code'}
    >
      <div className="wg-redeem">
        <div className="wg-redeem-segments">
          <span className="wg-redeem-prefix">WG-</span>
          {segments.map((seg, idx) => (
            <div key={idx} style={{ display: 'contents' }}>
              <input
                ref={refs[idx]}
                className="wg-redeem-segment"
                value={seg}
                onChange={e => handleSegmentChange(idx, e.target.value)}
                onKeyDown={e => handleKeyDown(idx, e)}
                maxLength={4}
                inputMode="text"
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
              />
              {idx < 3 && <span className="wg-redeem-dash">-</span>}
            </div>
          ))}
        </div>

        {error && <div className="wg-redeem-error">{error}</div>}
        {success && (
          <div className="wg-redeem-success">
            +{success.amount}{' '}
            {success.asset === 'voice_seconds'
              ? 'seconds added'
              : success.asset}
          </div>
        )}

        <div className="wg-redeem-actions">
          <button
            type="button"
            className="wg-redeem-btn"
            onClick={onClose}
            disabled={loading}
          >
            {t('CANCEL') || 'Cancel'}
          </button>
          <button
            type="button"
            className="wg-redeem-btn primary"
            onClick={handleRedeem}
            disabled={loading || segments.some(s => s.length !== 4)}
          >
            {loading ? t('LOADING') || 'Redeeming…' : t('REDEEM') || 'Redeem'}
          </button>
        </div>

        <p className="wg-redeem-helper">
          {t('REDEEM_HELPER') ||
            'Enter the 16-character code from your gift, voucher, or invite.'}
        </p>
      </div>
    </Modal>
  );
}
